import type { TextureDataLayout, TextureSource } from '@use-gpu/core';

import { useMemo, useOne, useResource } from '@use-gpu/live';

import { seq, makeTextureReadbackDataLayout, getTextureStorageType, getUniformArrayType } from '@use-gpu/core';

import { getScratchSource } from '../hooks/useScratchSource';
import { useDeviceContext } from '../providers/device-provider';

const READBACK_SOURCE = { flags: GPUBufferUsage?.COPY_DST | GPUBufferUsage?.MAP_READ, volatile: true };

export const useReadbackTexture = (
  source: TextureSource,
  buffers: number = 3,
) => {
  const device = useDeviceContext();
  const format = getTextureStorageType(source.format);

  const storages = useMemo(() =>
    seq(buffers).map(() => getScratchSource(device, format, READBACK_SOURCE)),
    [buffers, device, format]
  );

  let requested = -1;
  let layout: TextureDataLayout | null = null;
  const mapped = useOne(() => seq(buffers).map(() => false));

  let cancelled = false;
  useResource((dispose) => dispose(() => cancelled = true));

  const dispatchCopy = () => {
    if (cancelled) return null;

    const i = requested = mapped.indexOf(false);
    if (i >= 0) {
      const [storage, allocate] = storages[i];

      const [width, height] = source.size;
      layout = makeTextureReadbackDataLayout(source.size, source.format);

      const {bytesPerRow, itemsPerRow, rowsPerImage} = layout;
      allocate(itemsPerRow * rowsPerImage);

      const commandEncoder = device.createCommandEncoder();
      commandEncoder.copyTextureToBuffer(
        {texture: source.texture},
        {buffer: storage.buffer, bytesPerRow},
        {width, height}
      );
      return commandEncoder.finish();
    }
    else {
      console.warn(`Ran out of readback buffers`);
      return null;
    }
  };

  const asyncRead = async () => {
    const i = requested;
    if (i >= 0) {
      const [storage] = storages[i];
      const {buffer} = storage;

      mapped[i] = true;
      await buffer.mapAsync(GPUMapMode.READ);

      if (cancelled) {
        buffer.unmap();
        mapped[i] = false;

        return null;
      }

      const ctor = getUniformArrayType(format);
      const array = new ctor(buffer.getMappedRange());

      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const {itemsPerRow, dimsPerItem} = layout!;
      const [width, height] = source.size;

      const data = new ctor(width * height * dimsPerItem);

      let o = 0;
      for (let y = 0; y < height; ++y) {
        for (let x = 0; x < width; ++x) {
          const base = (x + y * itemsPerRow) * dimsPerItem;
          for (let i = 0; i < dimsPerItem; ++i) {
            data[o++] = array[base + i];
          }
        }
      }

      buffer.unmap();
      mapped[i] = false;

      return data;
    }

    return null;
  };

  return {dispatchCopy, asyncRead};
};
